import React, { useState, useCallback, useMemo } from "react";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import styles from "./invite-user-modal.module.scss";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import { PhoneInput } from "@whatslly/ui-components/v2/BaseElements/PhoneInput";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import type { UserSupportedLanguageType } from "@whatslly/types/User";
import { UserDefaultChannels } from "@whatslly/types/User";
import type { SelectOption } from "@whatslly/types/UI-V2/BaseElements/SelectType";
import { getUserSettingLanguageFromI18n } from "@whatslly/commons/LanguageChangeUtils";
import isEmail from "validator/es/lib/isEmail";
import { InvitePlatformSwitch } from "../components/InvitePlatformSwitch";
import { InviteDesktopExtensionsSwitch } from "../components/InviteDesktopExtensionsSwitch";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IUserManagementDataLayer } from "../types/IUserManagementDataLayer";
import { UserManagementDataLayerSymbol } from "../stores/UserManagementDataLayerSymbol";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { InviteRemoteSyncSwitch } from "../components/InviteRemoteSyncSwitch";
import type { SupportedSidecarChannel } from "@whatslly/commons/sidecarUtils";
import { SidecarChannelType } from "@whatslly/types/sidecars-server/Sidecar";
import classnames from "classnames";
import type { PermissionInstanceConfigs } from "@whatslly/commons/platformPermissions";

const languages: SelectOption<{ value: UserSupportedLanguageType }>[] = [
  { label: "English", value: "ENGLISH" },
  { label: "Español", value: "SPANISH" },
  { label: "Português", value: "PORTUGUESE" },
];

type IInviteUserModalProps = IModalInstance<{
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  language: string;
  phone?: string;
  inviteToPlatform: boolean;
  inviteToDesktopAndExtensions: boolean;
  inviteToRemoteSync: boolean;
  defaultUserChannels: UserDefaultChannels | undefined;
  defaultUserPermission: string | undefined;
  defaultUserRemoteSyncChannels: Record<SidecarChannelType, boolean> | undefined;
}>;

export const InviteUserModal: React.FC<
  IInviteUserModalProps & {
    instanceConfigs?: PermissionInstanceConfigs;
    canCreateSidecar: boolean;
    availableChannelsForCreation: { label: string; value: SupportedSidecarChannel }[];
  }
> = observer(({ closeModal, canCreateSidecar, availableChannelsForCreation, instanceConfigs }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("");
  const [inviteToPlatform, setInviteToPlatform] = useState(false);
  const [inviteToDesktopAndExtensions, setInviteToDesktopAndExtensions] = useState(false);
  const [inviteToRemoteSync, setInviteToRemoteSync] = useState(false);
  const [defaultUserChannels, setDefaultUSerChannels] = useState<UserDefaultChannels | undefined>(undefined);
  const [defaultUserRemoteSyncChannels, setDefaultUserRemoteSyncChannels] = useState<Record<SidecarChannelType, boolean> | undefined>(undefined);
  const [defaultUserPermission, setDefaultUserPermission] = useState<string | undefined>(undefined);
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const dataLayer = useDependencyInjector<IUserManagementDataLayer>(UserManagementDataLayerSymbol);

  const selectedOrgConfigs = dataLayer.orgConfigs?.state === "fulfilled" ? dataLayer.orgConfigs.value : undefined;
  const [language, setLanguage] = useState(
    languages.find((lang) => lang.value === getUserSettingLanguageFromI18n(i18n.language))?.value ?? languages[0].value
  );

  const hasAnyUserType = useMemo(
    () =>
      inviteToPlatform ||
      (inviteToDesktopAndExtensions && Object.values(defaultUserChannels ?? {}).some((value) => value)) ||
      (inviteToRemoteSync && Object.values(defaultUserRemoteSyncChannels ?? {}).some((value) => value)),
    [defaultUserChannels, defaultUserRemoteSyncChannels, inviteToDesktopAndExtensions, inviteToPlatform, inviteToRemoteSync]
  );
  const isDisabled = useMemo(
    () => !hasAnyUserType || !email || !isEmail(email) || !firstName || !lastName || !language,
    [email, firstName, hasAnyUserType, language, lastName]
  );

  const submit = useCallback(() => {
    if (!email || !firstName || !lastName || !language) {
      return;
    }
    closeModal({
      email,
      username,
      firstName,
      language,
      lastName,
      phone: phoneCountryCode && phone ? `+${phoneCountryCode}${phone}` : undefined,
      inviteToPlatform,
      inviteToDesktopAndExtensions,
      defaultUserChannels,
      defaultUserPermission,
      inviteToRemoteSync,
      defaultUserRemoteSyncChannels,
    });
  }, [
    email,
    firstName,
    lastName,
    language,
    closeModal,
    username,
    phone,
    phoneCountryCode,
    inviteToPlatform,
    inviteToDesktopAndExtensions,
    defaultUserChannels,
    defaultUserPermission,
    inviteToRemoteSync,
    defaultUserRemoteSyncChannels,
  ]);

  const onKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !isDisabled) {
        submit();
      }
    },
    [isDisabled, submit]
  );

  return (
    <BaseModal
      className={styles.modal}
      bodyClassName={styles.body}
      closeOnClickOutside={false}
      closeOnEscape={false}
      closeModal={() => closeModal()}
      header={<ModalHeader closeModal={() => closeModal()}>{t("user_management.invite_user_modal.title")}</ModalHeader>}
    >
      <div className={classnames(styles.form, { [styles.overflow]: inviteToPlatform })}>
        <TextInput
          type="text"
          value={email}
          placeholder={t("user_management.invite_user_modal.email")}
          removeClear={true}
          data-testid="user-email"
          onKeyPress={onKeyPress}
          onChange={(e) => setEmail(e.target.value.trim())}
        />
        <TextInput
          type="text"
          value={username}
          placeholder={t("user_management.invite_user_modal.username")}
          removeClear={true}
          data-testid="user-username"
          onKeyPress={onKeyPress}
          onChange={(e) => setUsername(e.target.value.trim())}
        />
        <TextInput
          type="text"
          value={firstName}
          placeholder={t("user_management.invite_user_modal.first_name")}
          removeClear={true}
          data-testid="user-first-name"
          onKeyPress={onKeyPress}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextInput
          type="text"
          value={lastName}
          placeholder={t("user_management.invite_user_modal.last_name")}
          removeClear={true}
          data-testid="user-last-name"
          onKeyPress={onKeyPress}
          onChange={(e) => setLastName(e.target.value)}
        />
        <div className={styles.phoneInputContainer}>
          <PhoneInput
            countryCode={phoneCountryCode}
            phoneNumberWithOutCountryCode={phone}
            onPhoneChange={setPhone}
            onCountryCodeChange={setPhoneCountryCode}
            testid="user-phone"
            disableCountryCodeAutoPick={!phoneCountryCode}
            disableBackgroundColor
            autoFocus={false}
          />
        </div>
        <Select<SelectOption<{ value: UserSupportedLanguageType }>>
          paddingInline={"none"}
          name={"calendarType"}
          value={languages.find((l) => l.value === language)}
          data-testid="user-language"
          onChange={(val: SelectOption<{ value: UserSupportedLanguageType }>) => setLanguage(val?.value)}
          options={languages}
        />
        <div className={styles.switchContainer}>
          <InviteDesktopExtensionsSwitch
            orgConfigs={selectedOrgConfigs?.configs}
            updateDefaultChannels={(channel, newStatus) =>
              setDefaultUSerChannels((defaultChannels) => ({ ...(defaultChannels || {}), [channel]: newStatus }))
            }
            value={inviteToDesktopAndExtensions}
            onChange={setInviteToDesktopAndExtensions}
          />
          {canCreateSidecar && availableChannelsForCreation?.length > 0 && (
            <InviteRemoteSyncSwitch
              availableChannelsForCreation={availableChannelsForCreation}
              value={inviteToRemoteSync}
              onChange={setInviteToRemoteSync}
              updateRemoteSyncChannels={(channel, newStatus) =>
                setDefaultUserRemoteSyncChannels((defaultChannels) => ({
                  ...(defaultChannels || { telegram: false, whatsapp: false, line: false, signal: false }),
                  [channel]: newStatus,
                }))
              }
            />
          )}
          <InvitePlatformSwitch
            instanceConfigs={instanceConfigs}
            value={inviteToPlatform}
            onChange={setInviteToPlatform}
            user={userStore.user}
            setPermissions={setDefaultUserPermission}
          />
        </div>
      </div>
      <ModalButtons testId="modal-buttons-submit-button" onCancel={() => closeModal()} isSubmitDisabled={isDisabled} onSubmit={submit} />
    </BaseModal>
  );
});
